// Session AAY (Loop 4): a genuine short summary of a project scope of works, for the office
// Details card. The stored scopeSummary is often a full itemised, per-lift scope (one bullet per
// work item, each repeating the lift name), which reads as a wall of repeated text. This condenses
// it into a genuine one or two sentence line: bullet/number markers and repeated "Lift ELE-NNN:"
// prefixes are stripped, identical work descriptions across lifts are de-duplicated, and the
// distinct items are joined into a flowing line with any overflow noted ("and N further items").
//
// This is DISPLAY ONLY. The full itemised scopeSummary is preserved verbatim (it is the exact text
// the quotation PDF prints), and is revealed by the card's expand toggle. Pure and deterministic so
// it is testable and never depends on an AI call.

// Strip a leading bullet / numbering marker and a repeated lift/unit label from one scope line,
// so "- Lift ELE-074: Replace the controller" becomes "Replace the controller".
function stripScopeItemPrefix(line: string): string {
  let s = line.trim();
  // Leading bullet or numbering: "-", "*", "•", "1.", "2)", etc.
  s = s.replace(/^[-*•·]+\s*/, "");
  s = s.replace(/^\d+[.)]\s*/, "");
  // A leading "label:" that looks like a lift/unit reference (has a digit or the words
  // lift/ele/car/unit/door), e.g. "Lift ELE-074:", "Car 1:", "Unit 2:".
  s = s.replace(/^([^:]{1,40}):\s*/, (m, label: string) => (/\d|lift|ele|car|unit|door/i.test(label) ? "" : m));
  // Or a leading "Lift ELE-074 - ..." style label separated by a dash.
  s = s.replace(/^((?:lift|car|unit|door)\s+[\w/-]+)\s+[-–—]\s+/i, "");
  return s.trim();
}

// The first one or two sentences of a prose paragraph, capped in length. A paragraph of more than
// two sentences is condensed to its first two; a very long single sentence falls back to a plain
// length cap.
function firstSentences(text: string, cap: number, maxSentences = 2): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  const sentences = trimmed.match(/[^.!?]+[.!?]+/g);
  if (sentences && sentences.length > maxSentences) {
    let out = "";
    let count = 0;
    for (const s of sentences) {
      if (count >= maxSentences) break;
      if (out.length + s.length > cap && out.length > 0) break;
      out += s;
      count += 1;
    }
    if (out.trim()) return out.trim();
  }
  if (trimmed.length <= cap) return trimmed;
  return trimmed.slice(0, cap).trimEnd() + "...";
}

export function summariseScope(raw: string): string {
  const text = (raw ?? "").trim();
  if (!text) return "";

  const lines = text.split(/\r?\n+/).map((l) => l.trim()).filter(Boolean);
  const items: string[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    const s = stripScopeItemPrefix(line).replace(/[.;,]+$/, "").trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(s);
  }

  // A single item, or a prose paragraph with no itemisation: summarise as the first sentence(s).
  if (items.length <= 1) return firstSentences(text, 220);

  // Multiple distinct items: join them into one line, capped, with any overflow noted.
  const CAP = 200;
  const kept: string[] = [];
  let len = 0;
  for (const it of items) {
    if (kept.length > 0 && len + it.length + 2 > CAP) break;
    kept.push(it);
    len += it.length + 2;
  }
  let summary = kept.join("; ");
  const remaining = items.length - kept.length;
  if (remaining > 0) summary += `; and ${remaining} further item${remaining === 1 ? "" : "s"}`;
  return /[.!?]$/.test(summary) ? summary : summary + ".";
}

// Whether a derived summary is materially shorter than its source (so it is worth showing a
// summary + expand rather than the whole text). A summary that is not shorter adds nothing.
export function scopeSummaryIsShorter(raw: string, summary: string): boolean {
  const source = (raw ?? "").trim();
  return summary.length > 0 && summary.length < source.length;
}
